import type { RGBColor } from "react-color";
import type { StyleLibrary } from "schemas/generated/project";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import twMerge from "@replo/design-system/utils/twMerge";
import { PickerComponent } from "@v2/components/CustomPicker";
import {
  BRAND_COLOR_KEYS,
  getFormattedColor,
  getFormattedColorWithoutOpacity,
  getHex8Color,
  getHexColor,
  getInitialColorValue,
  getRGB,
  isBrandColor,
  isNeutralColor,
} from "@v2/utils/colors";
import { ArrowLeft, ChevronUp, Pencil, Plus } from "lucide-react";

import { DesignLibraryIcon } from "./designLibraryIcon";
import { useDesignLibraryColors } from "./useDesignLibraryColors";

interface DesignLibraryColorsProps {
  styleLibrary: StyleLibrary | null;
  onBack?: () => void;
  onPresetsClick?: () => void;
  onStyleLibraryChange: (styleLibrary: StyleLibrary) => void;
}

export const DesignLibraryColors: React.FC<DesignLibraryColorsProps> = ({
  styleLibrary,
  onBack,
  onPresetsClick,
  onStyleLibraryChange,
}) => {
  const [expandedColor, setExpandedColor] = React.useState<string | null>(null);

  const [pickerColor, setPickerColor] = React.useState<RGBColor>(
    getInitialColorValue(null),
  );

  // Keep this for update functions that need direct access to the raw structure
  const colorsFromProjectStyleLibrary = styleLibrary?.colors;

  const [hexInputValue, setHexInputValue] = React.useState<string>("#000000");

  const [nameInputValue, setNameInputValue] = React.useState<string>("");

  const handleColorChange = (rgb: RGBColor) => {
    setPickerColor(rgb);
    setHexInputValue(getFormattedColor(getHexColor(rgb)) ?? "#000000");
  };

  const handleNameCommit = async () => {
    if (!expandedColor || !styleLibrary) {
      return;
    }

    const updatedColors = {
      neutrals: {
        light: colorsFromProjectStyleLibrary?.neutrals?.light ?? "",
        subtle: colorsFromProjectStyleLibrary?.neutrals?.subtle ?? "",
        muted: colorsFromProjectStyleLibrary?.neutrals?.muted ?? "",
        dark: colorsFromProjectStyleLibrary?.neutrals?.dark ?? "",
      },
      brand: {
        primary: colorsFromProjectStyleLibrary?.brand?.primary ?? {
          color: "",
          name: "Primary",
        },
        secondary: colorsFromProjectStyleLibrary?.brand?.secondary ?? {
          color: "",
          name: "Secondary",
        },
        tertiary: colorsFromProjectStyleLibrary?.brand?.tertiary ?? {
          color: "",
          name: "Tertiary",
        },
        accent: colorsFromProjectStyleLibrary?.brand?.accent ?? {
          color: "",
          name: "Accent",
        },
      },
    };

    updatedColors.brand[expandedColor as keyof typeof updatedColors.brand] = {
      ...updatedColors.brand[expandedColor as keyof typeof updatedColors.brand],
      name: nameInputValue,
    };

    onStyleLibraryChange({
      ...styleLibrary,
      colors: updatedColors,
    });
  };

  const handleColorCommit = async () => {
    if (!expandedColor || !styleLibrary) {
      return;
    }

    const newColorValue = getFormattedColor(getHex8Color(pickerColor));
    if (!newColorValue) {
      return;
    }

    const updatedColors = {
      neutrals: {
        light: colorsFromProjectStyleLibrary?.neutrals?.light ?? "",
        subtle: colorsFromProjectStyleLibrary?.neutrals?.subtle ?? "",
        muted: colorsFromProjectStyleLibrary?.neutrals?.muted ?? "",
        dark: colorsFromProjectStyleLibrary?.neutrals?.dark ?? "",
      },
      brand: {
        primary: colorsFromProjectStyleLibrary?.brand?.primary ?? {
          color: "",
          name: "Primary",
        },
        secondary: colorsFromProjectStyleLibrary?.brand?.secondary ?? {
          color: "",
          name: "Secondary",
        },
        tertiary: colorsFromProjectStyleLibrary?.brand?.tertiary ?? {
          color: "",
          name: "Tertiary",
        },
        accent: colorsFromProjectStyleLibrary?.brand?.accent ?? {
          color: "",
          name: "Accent",
        },
      },
    };

    if (isNeutralColor(expandedColor)) {
      updatedColors.neutrals[
        expandedColor as keyof typeof updatedColors.neutrals
      ] = newColorValue;
    } else if (isBrandColor(expandedColor)) {
      updatedColors.brand[expandedColor as keyof typeof updatedColors.brand] = {
        ...updatedColors.brand[
          expandedColor as keyof typeof updatedColors.brand
        ],
        color: newColorValue,
      };
    }

    onStyleLibraryChange({
      ...styleLibrary,
      colors: updatedColors,
    });
  };

  const handleHexInputCommit = async () => {
    const newColor = getRGB(hexInputValue);
    if (newColor) {
      setPickerColor(newColor);
      await handleColorCommit();
    } else {
      setHexInputValue(
        getFormattedColor(getHexColor(pickerColor)) ?? "#000000",
      );
    }
  };

  const {
    neutralColors,
    brandColors: brandColorsFromProjectStyleLibrary,
    hasAllBrandColors,
  } = useDesignLibraryColors(styleLibrary);

  const handleEditColor = async (colorKey: string) => {
    const newExpandedColor = expandedColor === colorKey ? null : colorKey;

    if (expandedColor === colorKey && newExpandedColor === null) {
      if (expandedColor && isBrandColor(expandedColor)) {
        await handleNameCommit();
      }
      await handleColorCommit();
    }

    if (
      expandedColor &&
      newExpandedColor !== expandedColor &&
      newExpandedColor !== null
    ) {
      if (isBrandColor(expandedColor)) {
        await handleNameCommit();
      }
      await handleColorCommit();
    }

    setExpandedColor(newExpandedColor);

    if (newExpandedColor) {
      let colorValue = "#FFFFFF";
      let nameValue = "";
      if (isNeutralColor(newExpandedColor)) {
        colorValue =
          colorsFromProjectStyleLibrary?.neutrals?.[
            newExpandedColor as keyof typeof colorsFromProjectStyleLibrary.neutrals
          ] ?? "#FFFFFF";
      } else if (isBrandColor(newExpandedColor)) {
        colorValue =
          colorsFromProjectStyleLibrary?.brand?.[
            newExpandedColor as keyof typeof colorsFromProjectStyleLibrary.brand
          ]?.color ?? "#FFFFFF";
        nameValue =
          colorsFromProjectStyleLibrary?.brand?.[
            newExpandedColor as keyof typeof colorsFromProjectStyleLibrary.brand
          ]?.name ??
          newExpandedColor.charAt(0).toUpperCase() + newExpandedColor.slice(1);
      }
      const newColor = getRGB(colorValue);
      setPickerColor(newColor);
      setHexInputValue(getFormattedColor(getHexColor(newColor)) ?? "#000000");
      setNameInputValue(nameValue);
    }
  };

  const handleAddBrandColor = async () => {
    if (!styleLibrary || hasAllBrandColors) {
      return;
    }

    const nextColorKey = BRAND_COLOR_KEYS.find((key) => {
      return (
        !colorsFromProjectStyleLibrary?.brand?.[key]?.color ||
        colorsFromProjectStyleLibrary.brand[key].color.trim() === ""
      );
    });

    if (!nextColorKey) {
      return;
    }

    const updatedColors = {
      neutrals: {
        light: colorsFromProjectStyleLibrary?.neutrals?.light ?? "",
        subtle: colorsFromProjectStyleLibrary?.neutrals?.subtle ?? "",
        muted: colorsFromProjectStyleLibrary?.neutrals?.muted ?? "",
        dark: colorsFromProjectStyleLibrary?.neutrals?.dark ?? "",
      },
      brand: {
        primary: colorsFromProjectStyleLibrary?.brand?.primary ?? {
          color: "",
          name: "Primary",
        },
        secondary: colorsFromProjectStyleLibrary?.brand?.secondary ?? {
          color: "",
          name: "Secondary",
        },
        tertiary: colorsFromProjectStyleLibrary?.brand?.tertiary ?? {
          color: "",
          name: "Tertiary",
        },
        accent: colorsFromProjectStyleLibrary?.brand?.accent ?? {
          color: "",
          name: "Accent",
        },
      },
    };

    updatedColors.brand[nextColorKey] = {
      ...updatedColors.brand[nextColorKey],
      color: "#FFFFFF",
      name: nextColorKey.charAt(0).toUpperCase() + nextColorKey.slice(1),
    };

    onStyleLibraryChange({
      ...styleLibrary,
      colors: updatedColors,
    });

    setExpandedColor(nextColorKey);

    const defaultName =
      nextColorKey.charAt(0).toUpperCase() + nextColorKey.slice(1);
    setNameInputValue(defaultName);

    const newColor = getRGB("#FFFFFF");
    setPickerColor(newColor);
    setHexInputValue("#FFFFFF");
  };

  return (
    <div className="flex flex-col items-start flex-[1_0_0] self-stretch min-h-0 rounded-xl border-0.5 border-border bg-white shadow-sm">
      <div className="flex justify-between items-center self-stretch p-6 border-b-0.5 border-border">
        <div className="flex items-center gap-3">
          <Button
            icon
            variant="tertiary"
            size="sm"
            onClick={() => {
              onBack?.();
            }}
          >
            <ArrowLeft />
          </Button>
          <div className="text-base font-semibold">Colors</div>
        </div>
        <Button
          variant="secondary"
          size="sm"
          startEnhancer={<DesignLibraryIcon />}
          onClick={onPresetsClick}
        >
          Presets
        </Button>
      </div>
      <div className="flex flex-1 flex-col self-stretch overflow-y-auto no-scrollbar">
        <div className="flex flex-col gap-4 px-6 py-4 border-b-0.5 border-border">
          <div className="text-xs font-medium text-muted">Neutrals</div>
          <div className="flex flex-col">
            {neutralColors.map((color) => (
              <div
                key={color.key}
                className={twMerge(
                  "group transition-colors cursor-pointer",
                  expandedColor === color.key
                    ? "rounded-lg border border-border bg-white shadow-button-selectable p-2"
                    : "rounded-lg border border-transparent hover:bg-sheet p-2",
                )}
                onClick={() => {
                  void handleEditColor(color.key);
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={twMerge(
                      "w-21 rounded-lg border-0.5 border-black/10 flex-shrink-0 transition-all duration-300 ease-in-out",
                      expandedColor === color.key ? "h-75" : "h-12",
                    )}
                    style={{
                      backgroundColor:
                        expandedColor === color.key
                          ? getFormattedColor(getHex8Color(pickerColor)) ??
                            color.value
                          : color.value,
                    }}
                  />
                  <div className="flex flex-col flex-1">
                    <div
                      className={twMerge(
                        "flex flex-col",
                        expandedColor === color.key &&
                          "border-b-0.5 border-border pb-2",
                      )}
                    >
                      <div className="text-sm font-medium">{color.name}</div>
                      <div className="text-sm text-muted uppercase">
                        {getFormattedColorWithoutOpacity(color.value)}
                      </div>
                    </div>
                    <div
                      className={twMerge(
                        "grid overflow-hidden transition-[grid-template-rows,opacity,padding-top] duration-300 ease-in-out",
                        expandedColor === color.key
                          ? "pt-4 opacity-100 grid-rows-[1fr]"
                          : "pt-0 opacity-0 grid-rows-[0fr]",
                      )}
                    >
                      <div
                        className="flex flex-col gap-4 min-h-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Main Color Picker Area */}
                        <PickerComponent
                          color={pickerColor}
                          onChange={({ rgb }) => handleColorChange(rgb)}
                          onDragEnd={() => void handleColorCommit()}
                        />

                        {/* Hex Input */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted">Hex</span>
                          <Input
                            size="base"
                            value={hexInputValue}
                            onChange={(e) => setHexInputValue(e.target.value)}
                            onBlur={() => void handleHexInputCommit()}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                void handleHexInputCommit();
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button
                    icon
                    variant="secondary"
                    size="base"
                    UNSAFE_className={twMerge(
                      "transition-opacity",
                      expandedColor === color.key
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleEditColor(color.key);
                    }}
                  >
                    {expandedColor === color.key ? <ChevronUp /> : <Pencil />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-4 px-6 py-4 ">
          <div className="text-xs font-medium text-muted">Brand Colors</div>
          <div className="flex flex-col">
            {brandColorsFromProjectStyleLibrary.map((color) => (
              <div
                key={color.key}
                className={twMerge(
                  "group transition-colors cursor-pointer",
                  expandedColor === color.key
                    ? "rounded-lg border border-border bg-white shadow-button-selectable p-2"
                    : "rounded-lg border border-transparent hover:bg-sheet p-2",
                )}
                onClick={() => {
                  void handleEditColor(color.key);
                }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={twMerge(
                      "w-21 rounded-lg border-0.5 border-black/10 flex-shrink-0 transition-all duration-300 ease-in-out",
                      expandedColor === color.key ? "h-88" : "h-12",
                    )}
                    style={{
                      backgroundColor:
                        expandedColor === color.key
                          ? getFormattedColor(getHex8Color(pickerColor)) ??
                            color.value
                          : color.value,
                    }}
                  />
                  <div className="flex flex-col flex-1">
                    <div
                      className={twMerge(
                        "flex flex-col",
                        expandedColor === color.key &&
                          "border-b-0.5 border-border pb-2",
                      )}
                    >
                      <div className="text-sm font-medium">
                        {expandedColor === color.key
                          ? nameInputValue
                          : color.name}
                      </div>
                      <div className="text-sm text-muted">
                        {color.key.charAt(0).toUpperCase() + color.key.slice(1)}{" "}
                        •{" "}
                        {getFormattedColorWithoutOpacity(
                          color.value,
                        )?.toUpperCase()}
                      </div>
                    </div>
                    <div
                      className={twMerge(
                        "grid overflow-hidden transition-[grid-template-rows,opacity,padding-top] duration-300 ease-in-out",
                        expandedColor === color.key
                          ? "pt-4 opacity-100 grid-rows-[1fr]"
                          : "pt-0 opacity-0 grid-rows-[0fr]",
                      )}
                    >
                      <div
                        className="flex flex-col gap-4 min-h-0"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Main Color Picker Area */}
                        <PickerComponent
                          color={pickerColor}
                          onChange={({ rgb }) => handleColorChange(rgb)}
                          onDragEnd={() => void handleColorCommit()}
                        />

                        {/* Name Input - Only for brand colors */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted w-10">Name</span>
                          <Input
                            size="base"
                            value={nameInputValue}
                            onChange={(e) => setNameInputValue(e.target.value)}
                            onBlur={() => void handleNameCommit()}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                void handleNameCommit();
                              }
                            }}
                          />
                        </div>

                        {/* Hex Input */}
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted w-10">Hex</span>
                          <Input
                            size="base"
                            value={hexInputValue}
                            onChange={(e) => setHexInputValue(e.target.value)}
                            onBlur={() => void handleHexInputCommit()}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                void handleHexInputCommit();
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button
                    icon
                    variant="secondary"
                    size="base"
                    UNSAFE_className={twMerge(
                      "transition-opacity",
                      expandedColor === color.key
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleEditColor(color.key);
                    }}
                  >
                    {expandedColor === color.key ? <ChevronUp /> : <Pencil />}
                  </Button>
                </div>
              </div>
            ))}
            {!hasAllBrandColors && (
              <div
                className="flex items-center gap-3 cursor-pointer p-2 hover:bg-sheet rounded-lg group"
                onClick={() => void handleAddBrandColor()}
              >
                <div className="w-21 h-12 rounded-lg border border-dashed border-border flex items-center justify-center flex-shrink-0 group-hover:bg-white">
                  <Plus size={16} className="text-muted" />
                </div>
                <div className="text-sm font-medium p-1">Add a brand color</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
